import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { brand, featureFlags } from '../config/esanzaContent';
import { useWebsiteContent } from '../hooks/useWebsiteContent';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';

const primaryNav = [
  { path: '/platform', label: 'Platform', labelKey: 'nav.platform', pageKey: 'platform' },
  { path: '/field-intelligence', label: 'Field Intelligence', labelKey: 'nav.fieldIntelligence', pageKey: 'platform' },
  { path: '/traceability', label: 'Traceability', labelKey: 'nav.traceability', pageKey: 'traceability' },
  { path: '/marketplace', label: 'Marketplace', labelKey: 'nav.marketplace', pageKey: 'marketplace' },
  { path: '/about', label: 'About', labelKey: 'nav.about', pageKey: 'about' },
  { path: '/contact', label: 'Contact', labelKey: 'nav.contact', pageKey: 'contact' },
];

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { settings, pages } = useWebsiteContent();
  const { user, isAuthenticated, logout } = useAuth();
  const marketplaceVisible = Boolean(settings.marketplace_visible ?? featureFlags.marketplaceVisible);
  const isPageVisible = (pageKey: string) => pages[pageKey]?.visible !== false;

  const visiblePrimary = primaryNav.filter((link) => {
    if (link.pageKey === 'marketplace') return marketplaceVisible && isPageVisible('marketplace');
    return isPageVisible(link.pageKey);
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition ${scrolled ? 'border-b border-white/10 bg-[#071b12]/95 shadow-xl shadow-black/20 backdrop-blur-xl' : 'bg-gradient-to-b from-black/40 to-transparent'}`}>
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-3 px-5 py-3 lg:px-8">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img src={brand.logo} alt="eSanza" className="h-11 w-auto max-w-[168px] object-contain" />
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 lg:flex flex-1 justify-center">
          {visiblePrimary.map((link) => {
            const active = isActive(link.path);
            if ((link as any).action === 'external') {
              return (
                <a
                  key={link.path}
                  href={(link as any).href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative rounded-full px-4 py-2 text-sm font-semibold text-white/78 hover:bg-white/10 hover:text-white transition"
                >
                  {t(link.labelKey, link.label)}
                  <svg className="inline-block ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              );
            }
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`relative rounded-full px-4 py-2 text-sm font-semibold transition ${
                  active ? 'text-[#f3d978]' : 'text-white/78 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t(link.labelKey, link.label)}
                {active && <span className="absolute -bottom-1 left-1/2 h-1 w-6 -translate-x-1/2 rounded-full bg-[#d4af37]" />}
              </Link>
            );
          })}
        </div>

        {/* Desktop right side: cart, auth */}
        <div className="hidden items-center gap-2 lg:flex shrink-0">
          {/* Cart icon */}
          <Link
            to="/buy"
            className="relative p-2 rounded-full text-white/78 hover:bg-white/10 hover:text-white transition"
            aria-label="Shopping cart"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </Link>

          {isAuthenticated ? (
            <div className="relative ml-1">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20 transition"
              >
                <div className="w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span className="hidden xl:inline">{user?.full_name?.split(' ')[0] || 'Account'}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-64 bg-[#0a2418]/95 rounded-2xl shadow-2xl border border-white/10 py-2 z-50 backdrop-blur-xl">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="text-sm font-bold text-white">{user?.full_name}</p>
                      <p className="text-xs text-white/50 mt-0.5">{user?.email}</p>
                    </div>
                    <div className="px-4 py-2 border-b border-white/10">
                      <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white/40">Buyer Portal</p>
                    </div>
                    <Link to="/account" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-[#f3d978] hover:bg-white/10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                      Dashboard
                    </Link>
                    <Link to="/account/orders" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      Orders
                    </Link>
                    <Link to="/account/rfqs" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      RFQs
                    </Link>
                    <Link to="/account/quotations" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>
                      Quotations
                    </Link>
                    <Link to="/account/deliveries" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                      Deliveries
                    </Link>
                    <Link to="/account/invoices" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>
                      Invoices
                    </Link>
                    <Link to="/account/organisation" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                      Organisation
                    </Link>
                    <Link to="/account/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      Profile
                    </Link>
                    <div className="border-t border-white/10 mt-1 pt-1">
                      <button onClick={() => { logout(); setUserMenuOpen(false); }} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-400 hover:bg-white/10 w-full text-left">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 ml-1">
              <Link to="/login" className="rounded-full px-4 py-2 text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition">
                {t('nav.login', 'Login')}
              </Link>
              <Link to="/try-esanza" className="rounded-full px-5 py-2 text-sm font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-emerald-900/30 transition">
                {t('nav.startWorkspace', 'Start Workspace')}
              </Link>
            </div>
          )}

          <div className="ml-1">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white lg:hidden"
          aria-label={t('header.toggle', 'Toggle navigation')}
        >
          <span className="text-xl">{open ? '×' : '☰'}</span>
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-white/10 bg-[#071b12] px-5 py-4 lg:hidden max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="mb-4 flex justify-end">
            <LanguageSwitcher />
          </div>
          <div className="grid gap-2">
            {visiblePrimary.map((link) => {
              if ((link as any).action === 'external') {
                return (
                  <a
                    key={link.path}
                    href={(link as any).href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10"
                  >
                    {t(link.labelKey, link.label)}
                    <svg className="inline-block ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                );
              }
              return (
                <Link key={link.path} to={link.path} className="flex items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold text-white/90 hover:bg-white/10">
                  <span>{t(link.labelKey, link.label)}</span>
                </Link>
              );
            })}

            {/* Mobile auth */}
            {isAuthenticated ? (
              <div className="mt-3 grid gap-2 border-t border-white/10 pt-3">
                <div className="px-4 py-1">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white/40">Buyer Portal</p>
                </div>
                <Link to="/account" className="rounded-xl px-4 py-3 text-sm font-semibold text-[#f3d978] hover:bg-white/10">Dashboard</Link>
                <Link to="/account/orders" className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10">Orders</Link>
                <Link to="/account/rfqs" className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10">RFQs</Link>
                <Link to="/account/quotations" className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10">Quotations</Link>
                <Link to="/account/deliveries" className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10">Deliveries</Link>
                <Link to="/account/invoices" className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10">Invoices</Link>
                <Link to="/account/organisation" className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10">Organisation</Link>
                <Link to="/account/profile" className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10">Profile</Link>
                <button onClick={logout} className="rounded-xl px-4 py-3 text-sm font-semibold text-red-400 hover:bg-white/10 text-left">Sign Out</button>
              </div>
            ) : (
              <div className="mt-3 grid gap-2 border-t border-white/10 pt-3">
                <Link to="/login" className="rounded-xl px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/10">{t('nav.login', 'Login')}</Link>
                <Link to="/try-esanza" className="rounded-xl px-4 py-3 text-sm font-bold bg-emerald-600 text-white text-center">{t('nav.startWorkspace', 'Start Workspace')}</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
